// UI illustration placeholders (art-assets §6.1): mode icons, the party doodle, how-to doodles,
// the spinner, the "soon" sticker, the round-summary and BR art, rank badges, the party arrow, the
// laggy turtle, the joystick pair and the offline backdrop. The card and button frames are CSS in
// the running game; their functions here are templates for `npm run export:placeholders`.
//
// Balls use the default arena look (solid fill, darker same-hue rim); lines are hand-jittered ink.

import { ARENA_COLORS, mixRgb, type Rgb, SPECIAL_COLORS, TEAM_COLORS } from '@ballio/shared';
import type { Canvas2D, Ctx2D } from '../../render/canvas.js';
import { noise01 } from '../../render/noise.js';
import { css } from '../../render/theme.js';
import {
  art,
  drawBall,
  drawFace,
  drawLabel,
  fillWith,
  INK,
  inkCirclePath,
  inkLinePath,
  inkPolyPath,
  paletteColor,
  spikyPath,
  strokeInk,
  TAU,
  WHITE,
} from './draw.js';

/** Scales a design drawn in a `base × base` box to the canvas. */
function boxed(
  base: number,
  draw: (ctx: Ctx2D) => void,
): (ctx: Ctx2D, w: number, h: number) => void {
  return (ctx, w, h) => {
    const k = Math.min(w, h) / base;
    ctx.setTransform(k, 0, 0, k, (w - base * k) / 2, (h - base * k) / 2);
    draw(ctx);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  };
}

function speedLines(
  ctx: Ctx2D,
  x: number,
  y: number,
  len: number,
  gap: number,
  count: number,
  seed: number,
): void {
  for (let i = 0; i < count; i++) {
    const yy = y + (i - (count - 1) / 2) * gap;
    ctx.beginPath();
    inkLinePath(ctx, x, yy, x + len * (i % 2 ? 0.7 : 1), yy, seed + i, 1.5);
    strokeInk(ctx, 7);
  }
}

// --- mode icons (256 × 256) -------------------------------------------------------------------

/** FFA: one big ball chasing a small one. */
export function drawModeFfa(w = 256, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(256, (ctx) => {
      drawBall(ctx, 62, 92, 30, paletteColor(6), { face: 'sad', seed: 1, rim: 0.1 });
      drawBall(ctx, 150, 150, 74, paletteColor(0), { face: 'grin', seed: 2, rim: 0.08 });
      speedLines(ctx, 210, 160, 36, 18, 3, 3);
    }),
  );
}

/** Battle Royale: a dashed shrinking circle. */
export function drawModeBattleRoyale(w = 256, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(256, (ctx) => {
      ctx.beginPath();
      ctx.arc(128, 128, 110, 0, TAU);
      fillWith(ctx, 0xe5484d, 0.12);
      ctx.setLineDash([22, 16]);
      ctx.beginPath();
      inkCirclePath(ctx, 128, 128, 108, 11, 0.01);
      strokeInk(ctx, 9);
      ctx.setLineDash([]);
      ctx.beginPath();
      inkCirclePath(ctx, 128, 128, 62, 12, 0.015);
      strokeInk(ctx, 10);
      drawBall(ctx, 112, 118, 20, paletteColor(4), { seed: 13 });
      drawBall(ctx, 150, 146, 14, paletteColor(10), { seed: 14 });
      for (const [x, y] of [
        [70, 70],
        [190, 84],
        [196, 190],
      ] as const) {
        ctx.beginPath();
        inkLinePath(ctx, x - 9, y - 9, x + 9, y + 9, x, 1);
        inkLinePath(ctx, x + 9, y - 9, x - 9, y + 9, y, 1);
        strokeInk(ctx, 6, INK, 0.7);
      }
    }),
  );
}

/** Teams: three balls in the team colours. */
export function drawModeTeams(w = 256, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(256, (ctx) => {
      const [a = 0xe5484d, b = 0x30a46c, c = 0x3e63dd] = TEAM_COLORS;
      drawBall(ctx, 80, 160, 54, a, { face: 'dots', seed: 21 });
      drawBall(ctx, 176, 160, 54, c, { face: 'dots', seed: 22 });
      drawBall(ctx, 128, 86, 54, b, { face: 'smile', seed: 23 });
    }),
  );
}

/** Experimental: a spawner doodle with pellets. */
export function drawModeExperimental(w = 256, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(256, (ctx) => {
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * TAU + 0.3;
        const rr = 96 + 14 * noise01(i, 5, 1);
        ctx.beginPath();
        ctx.arc(128 + rr * Math.cos(a), 128 + rr * Math.sin(a), 9 + 4 * noise01(i, 6, 1), 0, TAU);
        fillWith(ctx, paletteColor(i * 3));
      }
      ctx.beginPath();
      spikyPath(ctx, 128, 128, 66, 18, 0.12, 31, 0.02);
      fillWith(ctx, SPECIAL_COLORS.spawner);
      strokeInk(ctx, 7);
      ctx.setLineDash([12, 9]);
      ctx.beginPath();
      inkCirclePath(ctx, 128, 128, 46, 32, 0.01);
      strokeInk(ctx, 5, INK, 0.6);
      ctx.setLineDash([]);
    }),
  );
}

// --- party and how-to doodles -----------------------------------------------------------------

/** Party card: two balls holding hands (256 × 256). */
export function drawParty(w = 256, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(256, (ctx) => {
      ctx.beginPath();
      inkPolyPath(ctx, [88, 150, 112, 172, 144, 172, 168, 150], 41, 1.5);
      strokeInk(ctx, 9);
      drawBall(ctx, 70, 128, 50, paletteColor(11), { face: 'smile', seed: 42 });
      drawBall(ctx, 186, 128, 50, paletteColor(5), { face: 'smile', seed: 43 });
      ctx.beginPath();
      ctx.arc(128, 172, 9, 0, TAU);
      fillWith(ctx, INK);
    }),
  );
}

/** How-to: a ball with an arrow (240 × 240). */
export function drawHowtoMove(w = 240, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(240, (ctx) => {
      drawBall(ctx, 84, 140, 56, paletteColor(6), { face: 'dots', seed: 51 });
      ctx.beginPath();
      inkPolyPath(ctx, [124, 96, 158, 66, 196, 40], 52, 1.5);
      strokeInk(ctx, 10);
      ctx.beginPath();
      inkPolyPath(ctx, [168, 36, 198, 38, 192, 68], 53, 1, false);
      strokeInk(ctx, 10);
    }),
  );
}

/** How-to: a ball splitting (240 × 240). */
export function drawHowtoSplit(w = 240, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(240, (ctx) => {
      drawBall(ctx, 78, 132, 44, paletteColor(3), { face: 'dots', seed: 61 });
      drawBall(ctx, 166, 108, 44, paletteColor(3), { face: 'grin', seed: 62 });
      speedLines(ctx, 18, 132, 22, 16, 3, 63);
      ctx.setLineDash([10, 9]);
      ctx.beginPath();
      inkLinePath(ctx, 120, 150, 126, 86, 64, 1);
      strokeInk(ctx, 5, INK, 0.6);
      ctx.setLineDash([]);
    }),
  );
}

/** How-to: a ball with a small blob leaving it (240 × 240). */
export function drawHowtoShoot(w = 240, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(240, (ctx) => {
      drawBall(ctx, 92, 128, 62, paletteColor(9), { face: 'dots', seed: 71 });
      drawBall(ctx, 194, 92, 18, paletteColor(9), { seed: 72 });
      speedLines(ctx, 150, 112, 20, 12, 3, 73);
    }),
  );
}

// --- frames (templates; the game uses CSS) ----------------------------------------------------

/** `ui/frame.png`: a 96 × 96 nine-slice with 32 px corners: an ink border, transparent centre. */
export function drawFrameTemplate(w = 96, h = 96): Canvas2D | null {
  return art(w, h, (ctx, cw, ch) => {
    const inset = 6;
    ctx.beginPath();
    inkPolyPath(
      ctx,
      [inset, inset, cw - inset, inset, cw - inset, ch - inset, inset, ch - inset],
      81,
      0.8,
      true,
    );
    strokeInk(ctx, 4);
  });
}

/** `ui/button-frame.png`: 120 × 60 with 24 px slices: an ink outline only, radius 14. */
export function drawButtonFrameTemplate(w = 120, h = 60): Canvas2D | null {
  return art(w, h, (ctx, cw, ch) => {
    const i = 4;
    const r = 14;
    ctx.beginPath();
    ctx.moveTo(i + r, i);
    ctx.arcTo(cw - i, i, cw - i, ch - i, r);
    ctx.arcTo(cw - i, ch - i, i, ch - i, r);
    ctx.arcTo(i, ch - i, i, i, r);
    ctx.arcTo(i, i, cw - i, i, r);
    ctx.closePath();
    strokeInk(ctx, 4);
  });
}

// --- spinner ----------------------------------------------------------------------------------

function scribbleArc(
  ctx: Ctx2D,
  cx: number,
  cy: number,
  r: number,
  from: number,
  to: number,
  seed: number,
  width: number,
): void {
  if (to <= from) return;
  const steps = Math.max(4, Math.round(((to - from) / TAU) * 72));
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const a = from + ((to - from) * i) / steps;
    const rr = r * (1 + 0.035 * Math.sin(a * 3 + seed) + 0.02 * (noise01(seed, i, 2) - 0.5));
    const x = cx + rr * Math.cos(a);
    const y = cy + rr * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  strokeInk(ctx, width);
}

/** `ui/spinner.png` (256 × 256, rotated by CSS): a scribbled ring with a gap. */
export function drawSpinner(w = 256, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(256, (ctx) => {
      scribbleArc(ctx, 128, 128, 96, -Math.PI / 2, -Math.PI / 2 + TAU * 0.8, 91, 18);
    }),
  );
}

/** `ui/spinner-strip.png` (2048 × 256): 8 frames of the ring drawn on, then erased. */
export function drawSpinnerStrip(w = 2048, h = 256): Canvas2D | null {
  return art(w, h, (ctx, cw, ch) => {
    const frames = 8;
    const fw = cw / frames;
    for (let i = 0; i < frames; i++) {
      const k = Math.min(fw, ch) / 256;
      ctx.setTransform(k, 0, 0, k, i * fw + (fw - 256 * k) / 2, (ch - 256 * k) / 2);
      const start = -Math.PI / 2;
      const on = i < 4 ? (i + 1) / 4 : 1;
      const off = i < 4 ? 0 : (i - 3) / 4.5;
      scribbleArc(ctx, 128, 128, 96, start + TAU * off, start + TAU * on, 91, 18);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  });
}

// --- sticker, summary and BR art --------------------------------------------------------------

/** `ui/sticker-soon.png` (128 × 64): a yellow tape strip with "soon". */
export function drawStickerSoon(w = 128, h = 64): Canvas2D | null {
  return art(w, h, (ctx, cw, ch) => {
    ctx.save();
    ctx.translate(cw / 2, ch / 2);
    ctx.rotate(-0.08);
    const tw = cw * 0.86;
    const th = ch * 0.62;
    const pts: number[] = [];
    // Torn (zigzag) ends.
    for (let i = 0; i <= 4; i++) pts.push(-tw / 2 + (i % 2 ? 4 : 0), -th / 2 + (th * i) / 4);
    for (let i = 4; i >= 0; i--) pts.push(tw / 2 - (i % 2 ? 4 : 0), -th / 2 + (th * i) / 4);
    ctx.beginPath();
    inkPolyPath(ctx, pts, 101, 0.6, true);
    fillWith(ctx, 0xf5c518, 0.92);
    strokeInk(ctx, 2, mixRgb(0xf5c518, INK, 0.5));
    drawLabel(ctx, 'soon', 0, 1, th * 0.68, INK, null);
    ctx.restore();
  });
}

/** `ui/death.png` (600 × 400): a squashed ball with X eyes under a bigger ball's shadow. */
export function drawDeathArt(w = 600, h = 400): Canvas2D | null {
  return art(w, h, (ctx, cw, ch) => {
    const k = Math.min(cw / 600, ch / 400);
    ctx.setTransform(k, 0, 0, k, (cw - 600 * k) / 2, (ch - 400 * k) / 2);
    ctx.beginPath();
    ctx.ellipse(300, 120, 230, 120, 0, 0, TAU);
    fillWith(ctx, INK, 0.12);
    ctx.save();
    ctx.translate(300, 300);
    ctx.scale(1.45, 0.62);
    drawBall(ctx, 0, 0, 92, paletteColor(0), { seed: 111 });
    ctx.restore();
    drawFace(ctx, 300, 296, 92, 'x', 112);
    // A few "squish" marks.
    for (const [x0, y0, x1, y1] of [
      [150, 248, 128, 232],
      [450, 248, 472, 232],
      [300, 222, 300, 200],
    ] as const) {
      ctx.beginPath();
      inkLinePath(ctx, x0, y0, x1, y1, x0 + y0, 1);
      strokeInk(ctx, 7);
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  });
}

/** `ui/br-win.png` (800 × 400): a confetti burst of tiny balls. */
export function drawBrWin(w = 800, h = 400): Canvas2D | null {
  return art(w, h, (ctx, cw, ch) => {
    const cx = cw / 2;
    const cy = ch * 0.55;
    for (let i = 0; i < 70; i++) {
      const a = noise01(i, 1, 9) * TAU;
      const d = Math.sqrt(noise01(i, 2, 9)) * Math.min(cw, ch * 2) * 0.46;
      const r = 6 + 12 * noise01(i, 3, 9);
      drawBall(ctx, cx + d * Math.cos(a), cy + d * Math.sin(a) * 0.55, r, paletteColor(i), {
        seed: i,
        rim: 0.12,
      });
    }
    drawBall(ctx, cx, cy, Math.min(cw, ch) * 0.16, paletteColor(22), { face: 'grin', seed: 201 });
  });
}

const RANK_COLORS: readonly Rgb[] = [0xf5c518, 0xc0c6cc, 0xc8853d];

/** `ui/rank-<n>.png` (256 × 256): a gold / silver / bronze rosette with the number. */
export function drawRankBadge(rank: 1 | 2 | 3, w = 256, h = w): Canvas2D | null {
  const color = RANK_COLORS[rank - 1] ?? 0xc0c6cc;
  return art(
    w,
    h,
    boxed(256, (ctx) => {
      for (const dx of [-28, 28]) {
        ctx.beginPath();
        inkPolyPath(
          ctx,
          [
            128 + dx * 0.4,
            150,
            128 + dx * 1.6,
            238,
            128 + dx * 0.9,
            222,
            128 + dx * 0.2,
            240,
            128 + dx * 0.1,
            160,
          ],
          300 + dx,
          1,
          true,
        );
        fillWith(ctx, mixRgb(color, 0xe5484d, 0.55));
        strokeInk(ctx, 5);
      }
      ctx.beginPath();
      spikyPath(ctx, 128, 112, 88, 16, 0.14, 310 + rank, 0.01);
      fillWith(ctx, color);
      strokeInk(ctx, 6, mixRgb(color, INK, 0.55));
      ctx.beginPath();
      inkCirclePath(ctx, 128, 112, 62, 320 + rank, 0.015);
      fillWith(ctx, mixRgb(color, WHITE, 0.3));
      strokeInk(ctx, 5, mixRgb(color, INK, 0.45));
      drawLabel(ctx, String(rank), 128, 116, 84, INK, null);
    }),
  );
}

/** `ui/arrow.png` (64 × 64, pointing right): a chunky arrow, white for the multiply tint. */
export function drawArrow(w = 64, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(64, (ctx) => {
      ctx.beginPath();
      inkPolyPath(ctx, [8, 24, 34, 24, 34, 10, 58, 32, 34, 54, 34, 40, 8, 40], 401, 0.6, true);
      fillWith(ctx, WHITE);
      strokeInk(ctx, 4.5);
    }),
  );
}

/** `ui/laggy.png` (64 × 64): a turtle doodle. */
export function drawLaggy(w = 64, h = w): Canvas2D | null {
  return art(
    w,
    h,
    boxed(64, (ctx) => {
      ctx.beginPath();
      ctx.arc(52, 36, 7, 0, TAU);
      fillWith(ctx, 0x7fd46b);
      strokeInk(ctx, 2.5);
      for (const x of [18, 40]) {
        ctx.beginPath();
        ctx.ellipse(x, 44, 5, 6, 0, 0, TAU);
        fillWith(ctx, 0x7fd46b);
        strokeInk(ctx, 2.5);
      }
      ctx.beginPath();
      ctx.ellipse(30, 38, 20, 15, 0, Math.PI, TAU);
      ctx.closePath();
      fillWith(ctx, 0x4fb286);
      strokeInk(ctx, 3);
      ctx.beginPath();
      inkPolyPath(ctx, [22, 36, 26, 28, 34, 28, 38, 36], 402, 0.4, true);
      strokeInk(ctx, 2);
      ctx.beginPath();
      ctx.arc(54, 34, 1.4, 0, TAU);
      fillWith(ctx, INK);
    }),
  );
}

/** `ui/joystick.png` (240 × 240): a ring at 50 % opacity, transparent centre. */
export function drawJoystick(w = 240, h = w): Canvas2D | null {
  return art(w, h, (ctx, cw, ch) => {
    const k = Math.min(cw, ch) / 240;
    ctx.beginPath();
    inkCirclePath(ctx, cw / 2, ch / 2, 112 * k, 501, 0.01);
    strokeInk(ctx, 6 * k, INK, 0.5);
  });
}

/** `ui/joystick-knob.png` (112 × 112): a filled dot at 60 % opacity. */
export function drawJoystickKnob(w = 112, h = w): Canvas2D | null {
  return art(w, h, (ctx, cw, ch) => {
    ctx.beginPath();
    inkCirclePath(ctx, cw / 2, ch / 2, Math.min(cw, ch) * 0.46, 502, 0.02);
    fillWith(ctx, INK, 0.6);
  });
}

/**
 * `ui/menu-backdrop.png` (1920 × 1080, opaque): the classic arena (near-white with a grey grid)
 * and 12 static balls, shown while no arena is connected.
 */
export function drawMenuBackdrop(w = 1920, h = 1080): Canvas2D | null {
  const colors = ARENA_COLORS.classic.light;
  return art(w, h, (ctx, cw, ch) => {
    ctx.fillStyle = css(colors.background);
    ctx.fillRect(0, 0, cw, ch);
    const step = Math.max(8, Math.round((50 * cw) / 1920));
    ctx.beginPath();
    for (let x = 0.5; x < cw; x += step) {
      ctx.moveTo(Math.round(x) + 0.5, 0);
      ctx.lineTo(Math.round(x) + 0.5, ch);
    }
    for (let y = 0.5; y < ch; y += step) {
      ctx.moveTo(0, Math.round(y) + 0.5);
      ctx.lineTo(cw, Math.round(y) + 0.5);
    }
    ctx.lineWidth = 1;
    ctx.strokeStyle = css(colors.grid);
    ctx.stroke();
    for (let i = 0; i < 90; i++) {
      ctx.beginPath();
      ctx.arc(
        noise01(i, 1, 77) * cw,
        noise01(i, 2, 77) * ch,
        (8 + 6 * noise01(i, 3, 77)) * (cw / 1920),
        0,
        TAU,
      );
      fillWith(ctx, paletteColor(i * 7));
    }
    const s = cw / 1920;
    for (let i = 0; i < 12; i++) {
      const r = (30 + 110 * noise01(i, 4, 78) ** 2) * s;
      const x = noise01(i, 5, 78) * cw;
      const y = noise01(i, 6, 78) * ch;
      drawBall(ctx, x, y, r, paletteColor(i * 5 + 1), { seed: 600 + i, rim: 0.06 });
    }
  });
}
